import React, { ReactElement, useEffect } from 'react';
import {
	TableHead,
	TableRow,
	TableCell,
	SortDirection
} from '@material-ui/core';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import { SortableTableHeaderCellProperty, SortOrder } from '../../../models';
import { CrudFilterInput } from '..';
import { connect } from 'react-redux';
import {
	AppState,
	AppThunkDispatch,
	CoMaFilterState,
	comaResetStates,
	comaSetFilter,
	comaSetSort,
	CoMaSortState,
	ContentManagerAction
} from '../../../redux';
import { useCallbackSafeProps } from '../../../hooks';

export interface ICRUDTableFilterProps {
	property?: string;
	value: string;
}
export interface ICRUDTableHeaderProps {
	headers: SortableTableHeaderCellProperty[];
	onChange: () => void;
}

export interface ICRUDTableHeaderStateProps {
	filterOptions: CoMaFilterState;
	sortOptions: CoMaSortState;
	setFilter: (property: string, value: string) => void;
	setSort: (property: string, order: SortOrder) => void;
	resetStates: () => void;
}

export function CrudTableHeaderView(
	props: ICRUDTableHeaderProps & ICRUDTableHeaderStateProps
): ReactElement {
	const {
		headers,
		filterOptions,
		sortOptions,
		setFilter,
		setSort,
		resetStates,
		onChange
	} = props;

	const [getSafeFilterOptions] =
		useCallbackSafeProps<CoMaFilterState>(filterOptions);
	const [getSafeSortOptions] =
		useCallbackSafeProps<CoMaSortState>(sortOptions);

	useEffect(() => resetStates, []);

	useEffect(() => {
		onChange();
	}, [getSafeFilterOptions(), getSafeSortOptions()]);

	function handleFilter(prop: string): (value: string) => void {
		return (value: string) => {
			if (getSafeFilterOptions().value !== value) setFilter(prop, value);
		};
	}

	function handleSort(prop: string) {
		const state: CoMaSortState = getSafeSortOptions();
		const isAsc = state.order === 'asc';
		setSort(prop, isAsc ? 'desc' : 'asc');
	}

	function isFilterDisabled(key: string): boolean {
		return (
			key !== getSafeFilterOptions().property &&
			filterOptions.value !== ''
		);
	}

	function getSortDirection(key: string): SortDirection | undefined {
		return getSafeSortOptions().property === key
			? getSafeSortOptions().order
			: false;
	}

	function getDirection(key: string): 'desc' | 'asc' | undefined {
		return getSafeSortOptions().property === key
			? getSafeSortOptions().order
			: 'asc';
	}

	function isSortingActive(key: string): boolean {
		return getSafeSortOptions().property === key;
	}

	return (
		<TableHead>
			{headers.find((x) => x.enableFiltering) && (
				<TableRow>
					{headers.map((header: SortableTableHeaderCellProperty) => (
						<TableCell key={header.key}>
							{header.enableFiltering ? (
								<CrudFilterInput
									disabled={isFilterDisabled(header.key)}
									placeholder={`${header.label} filter`}
									onFilter={handleFilter(header.key)}
								/>
							) : (
								<></>
							)}
						</TableCell>
					))}
					<TableCell></TableCell>
					<TableCell></TableCell>
				</TableRow>
			)}
			<TableRow>
				{headers.map((header: SortableTableHeaderCellProperty) => (
					<TableCell
						key={header.key}
						sortDirection={getSortDirection(header.key)}
					>
						<TableSortLabel
							active={isSortingActive(header.key)}
							direction={getDirection(header.key)}
							onClick={() => handleSort(header.key)}
							disabled={header.disableSort}
						>
							{header.label}
						</TableSortLabel>
					</TableCell>
				))}
				<TableCell></TableCell>
				<TableCell></TableCell>
			</TableRow>
		</TableHead>
	);
}

export function mapStateToProps(state: AppState) {
	return {
		filterOptions: state.contentManager.filter,
		sortOptions: state.contentManager.sort
	};
}

export function mapDispatchToProps(
	dispatch: AppThunkDispatch<ContentManagerAction>
) {
	return {
		setFilter: (property: string, value: string) =>
			dispatch(comaSetFilter(property, value)),
		setSort: (property: string, order: SortOrder) =>
			dispatch(comaSetSort(property, order)),
		resetStates: () => dispatch(comaResetStates())
	};
}

export const CrudTableHeader = connect(
	mapStateToProps,
	mapDispatchToProps
)(CrudTableHeaderView);
